import { Backdrop, CircularProgress } from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  X,
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "../../../API/newIndex";
import useUnsavedChanges from "../../../controllers/useUnsavedChanges";
import UnsavedChangesModal from "../../../controllers/UnsavedChangesModal";
import { ButtonComponent, InputComponent } from "@shopeasy-dashboard-ui/ui-library";
import Notification from "../../Notification";

const getChangedFields = (currentValues, originalValues) => {
  if (!currentValues || !originalValues) return currentValues || {};
  const changed = {};
  Object.keys(currentValues).forEach((key) => {
    const cur = currentValues[key];
    const orig = originalValues[key];
    if (typeof cur === "object" && cur !== null && typeof orig === "object" && orig !== null) {
      const sub = getChangedFields(cur, orig);
      if (Object.keys(sub).length > 0) {
        changed[key] = cur;
      }
    } else if (String(cur || "").trim() !== String(orig || "").trim()) {
      changed[key] = cur;
    }
  });
  return changed;
};

const BulkEditPosLocation = ({
  bulkLocationIds = [],
  onClose,
  onSuccess,
}) => {
  const [bulkLocationsList, setBulkLocationsList] = useState([]);
  const [activeBulkIndex, setActiveBulkIndex] = useState(0);
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const bulkLocationsDataRef = useRef({});

  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [btnLoading, setBtnLoading] = useState(false);
  const [formSpinner, setFormSpinner] = useState(false);

  // Exact Form State matching UpdatePosLocation.js
  const [name, setName] = useState("");
  const [addressData, setAddressData] = useState({
    addressLine1: "",
    addressLine2: "",
    country: "india",
    state: "",
    city: "",
    pincode: "",
    phone: "",
  });
  const [warn, setWarn] = useState(false);
  const [err, setErr] = useState(false);

  const originalDataRef = useRef(null);
  const originalPayloadRef = useRef(null);

  const buildPayload = (formData = {}) => {
    return {
      name: formData.name || "",
      address: {
        addressLine1: formData.addressData?.addressLine1 || "",
        addressLine2: formData.addressData?.addressLine2 || "",
        country: formData.addressData?.country || "india",
        state: formData.addressData?.state || "",
        city: formData.addressData?.city || "",
        pincode: formData.addressData?.pincode || "",
        phone: formData.addressData?.phone || "",
      },
    };
  };

  const currentFormValues = useMemo(() => {
    return {
      name,
      addressData,
    };
  }, [name, addressData]);

  const applyLocationToForm = (rawLoc) => {
    if (!rawLoc) return;

    let parsedAddress = {};
    if (typeof rawLoc?.address === "object" && rawLoc?.address !== null) {
      parsedAddress = rawLoc.address;
    } else if (typeof rawLoc?.address === "string" && rawLoc.address.trim().startsWith("{")) {
      try {
        parsedAddress = JSON.parse(rawLoc.address);
      } catch (e) {
        parsedAddress = {};
      }
    }

    const initialFormValues = {
      name: rawLoc?.name || "",
      addressData: {
        addressLine1: parsedAddress?.addressLine1 || rawLoc?.addressLine1 || "",
        addressLine2: parsedAddress?.addressLine2 || rawLoc?.addressLine2 || "",
        country: parsedAddress?.country || rawLoc?.country || "india",
        state: parsedAddress?.state || rawLoc?.state || "",
        city: parsedAddress?.city || rawLoc?.city || "",
        pincode: parsedAddress?.pincode || rawLoc?.pincode || "",
        phone: parsedAddress?.phone || rawLoc?.phone || "",
      },
    };

    originalDataRef.current = initialFormValues;
    originalPayloadRef.current = buildPayload(initialFormValues);

    setName(initialFormValues.name);
    setAddressData(initialFormValues.addressData);
    setWarn(false);
    setErr(false);
  };

  const fetchLocationsData = async () => {
    if (!bulkLocationIds || bulkLocationIds.length === 0) return;
    setFormSpinner(true);
    try {
      let res;
      try {
        res = await API({
          url: "/admin/pos/GetPOSLocationDataByIds",
          method: "post",
          data: { ids: bulkLocationIds, id: bulkLocationIds, locations: bulkLocationIds, location: bulkLocationIds },
        });
      } catch (postErr) {
        res = await API({
          url: "/admin/pos/GetPOSLocationDataByIds",
          method: "post",
          data: bulkLocationIds,
        });
      }

      let loadedRecords = [];
      if (res?.status === 200 || res?.status === 304) {
        const resultData = res?.data;
        loadedRecords =
          resultData?.data?.records ||
          resultData?.records ||
          (Array.isArray(resultData?.data) ? resultData.data : []) ||
          (Array.isArray(resultData) ? resultData : []);
      }

      if (loadedRecords.length > 0) {
        setBulkLocationsList(loadedRecords);
        applyLocationToForm(loadedRecords[0]);
      } else {
        setIsNotification(true);
        setNotificationMsg("No Location records found for bulk edit");
        setSeverity("error");
      }
    } catch (error) {
      console.error("Error fetching bulk Location data:", error);
      setIsNotification(true);
      setNotificationMsg(error?.response?.data?.message || error?.message || "Failed to fetch Location details");
      setSeverity("error");
    } finally {
      setFormSpinner(false);
    }
  };

  useEffect(() => {
    fetchLocationsData();
  }, []);

  const handleDataFromPincode = async (targetPincode) => {
    const pin = (targetPincode || addressData?.pincode || "").toString().trim();
    if (!pin || pin.length !== 6) return;

    try {
      const ApiCall = await API({
        url: `/super/admin/pincode/getDataFromPincode?pincode=${pin}`,
        method: "get",
      });
      if (ApiCall?.data) {
        const result = ApiCall?.data;
        setAddressData((prev) => ({
          ...prev,
          state: result?.data?.StateName || prev.state,
          city: result?.data?.District || prev.city,
        }));
      }
    } catch (error) {
      // Keep existing state & city if API fails
    }
  };

  const handleGetDataFromPincode = async () => {
    const currentPincode = (addressData?.pincode || "").toString().trim();
    const originalPincode = (originalDataRef.current?.addressData?.pincode || "").toString().trim();

    // If pincode is not 6 digits or has NOT changed from original, do nothing!
    if (!currentPincode || currentPincode.length !== 6 || currentPincode === originalPincode) {
      return;
    }

    try {
      const ApiCall = await API({
        url: `/super/admin/pincode/getLocationFromPincode?pincode=${currentPincode}`,
        method: "get",
      });
      if (ApiCall?.data) {
        const result = ApiCall?.data;
        setAddressData((prev) => ({
          ...prev,
          state: result?.data?.StateName || prev.state,
          city: result?.data?.District || prev.city,
        }));
      }
    } catch (error) {
      handleDataFromPincode(currentPincode);
    }
  };

  const saveCurrentLocationChangesToMemory = useCallback(() => {
    const activeItem = bulkLocationsList[activeBulkIndex];
    if (!activeItem?._id) return;

    const currentPayload = buildPayload(currentFormValues);
    const origPayload = originalPayloadRef.current || {};
    const changedFields = getChangedFields(currentPayload, origPayload);

    if (Object.keys(changedFields).length > 0) {
      bulkLocationsDataRef.current[activeItem._id] = {
        _id: activeItem._id,
        id: activeItem._id,
        values: { ...currentFormValues },
        originalPayload: origPayload,
        _hasChanged: true,
      };
    } else {
      delete bulkLocationsDataRef.current[activeItem._id];
    }
  }, [activeBulkIndex, bulkLocationsList, currentFormValues]);

  const switchActiveLocation = (targetIndex) => {
    if (targetIndex < 0 || targetIndex >= bulkLocationsList.length) return;
    if (targetIndex === activeBulkIndex) return;

    saveCurrentLocationChangesToMemory();

    const targetItem = bulkLocationsList[targetIndex];
    const cachedChanges = bulkLocationsDataRef.current[targetItem?._id];

    if (cachedChanges?.values) {
      let parsedAddress = {};
      if (typeof targetItem?.address === "object" && targetItem?.address !== null) {
        parsedAddress = targetItem.address;
      }
      const orig = {
        name: targetItem?.name || "",
        addressData: {
          addressLine1: parsedAddress?.addressLine1 || targetItem?.addressLine1 || "",
          addressLine2: parsedAddress?.addressLine2 || targetItem?.addressLine2 || "",
          country: parsedAddress?.country || targetItem?.country || "india",
          state: parsedAddress?.state || targetItem?.state || "",
          city: parsedAddress?.city || targetItem?.city || "",
          pincode: parsedAddress?.pincode || targetItem?.pincode || "",
          phone: parsedAddress?.phone || targetItem?.phone || "",
        },
      };
      originalDataRef.current = orig;
      originalPayloadRef.current = buildPayload(orig);

      setName(cachedChanges.values.name);
      setAddressData(cachedChanges.values.addressData);
      setWarn(false);
      setErr(false);
    } else {
      applyLocationToForm(targetItem);
    }

    setActiveBulkIndex(targetIndex);
  };

  const hasFormLevelChanges = useMemo(() => {
    if (!originalPayloadRef.current) return false;
    const currentPayload = buildPayload(currentFormValues);
    const changed = getChangedFields(currentPayload, originalPayloadRef.current);
    return Object.keys(changed).length > 0;
  }, [currentFormValues]);

  const isLocationModified = (loc, index) => {
    if (!loc) return false;
    if (bulkLocationsDataRef.current[loc._id]?._hasChanged) return true;
    if (index === activeBulkIndex && hasFormLevelChanges) return true;
    return false;
  };

  const modifiedCount = useMemo(() => {
    const memoryKeys = new Set(Object.keys(bulkLocationsDataRef.current));
    const activeLoc = bulkLocationsList[activeBulkIndex];
    if (activeLoc?._id && hasFormLevelChanges) {
      memoryKeys.add(activeLoc._id);
    } else if (activeLoc?._id && !hasFormLevelChanges && memoryKeys.has(activeLoc._id)) {
      memoryKeys.delete(activeLoc._id);
    }
    return memoryKeys.size;
  }, [bulkLocationsList, activeBulkIndex, hasFormLevelChanges]);

  const hasAnyBulkChanges = modifiedCount > 0;

  const {
    showUnsavedModal,
    handleRequestClose,
    handleConfirmLeave,
    handleStay,
  } = useUnsavedChanges(hasAnyBulkChanges, onClose);

  const handleSubmitBulkLocations = async (e) => {
    if (e) e.preventDefault();

    saveCurrentLocationChangesToMemory();

    const activeItem = bulkLocationsList[activeBulkIndex];
    if (activeItem?._id && hasFormLevelChanges) {
      const currentPayload = buildPayload(currentFormValues);
      bulkLocationsDataRef.current[activeItem._id] = {
        _id: activeItem._id,
        id: activeItem._id,
        values: { ...currentFormValues },
        originalPayload: originalPayloadRef.current,
        _hasChanged: true,
      };
    }

    const modifiedEntries = Object.entries(bulkLocationsDataRef.current).filter(
      ([, data]) => data && data._hasChanged
    );

    if (modifiedEntries.length === 0) {
      setIsNotification(true);
      setNotificationMsg("No changes detected to update");
      setSeverity("info");
      return;
    }

    for (const [, data] of modifiedEntries) {
      if (!data.values.name || !data.values.name.trim()) {
        setIsNotification(true);
        setNotificationMsg("All modified locations must have a Location Name");
        setSeverity("error");
        setWarn(true);
        setErr(true);
        return;
      }
    }

    setBtnLoading(true);
    setFormSpinner(true);
    try {
      const bulkPayload = modifiedEntries.map(([locId, data]) => {
        const payload = buildPayload(data.values);
        const baseline = data.originalPayload;
        const changed = getChangedFields(payload, baseline);
        const fieldsToUpdate = changed && Object.keys(changed).length > 0 ? changed : payload;

        return {
          _id: locId,
          id: locId,
          ...fieldsToUpdate,
        };
      });

      let res;
      try {
        res = await API({
          url: "/admin/pos/BulkUpdatePOSLocation",
          method: "post",
          data: {
            locations: bulkPayload,
            data: bulkPayload,
            location: bulkPayload,
          },
        });
      } catch (err) {
        res = await API({
          url: "/admin/pos/BulkUpdatePOSLocation",
          method: "post",
          data: bulkPayload,
        });
      }

      if (res?.status === 200 || res?.status === 304 || res?.data?.success) {
        setIsNotification(true);
        setNotificationMsg(
          `${bulkPayload.length} Location${bulkPayload.length > 1 ? "s" : ""} updated successfully!`
        );
        setSeverity("success");
        bulkLocationsDataRef.current = {};
        if (onSuccess) {
          onSuccess(bulkPayload.length);
        } else if (onClose) {
          onClose();
        }
      } else {
        throw new Error(res?.data?.message || "Failed to bulk update locations");
      }
    } catch (error) {
      console.error("Bulk update error:", error);
      setIsNotification(true);
      setNotificationMsg(error?.response?.data?.message || error?.message || "Error updating locations");
      setSeverity("error");
    } finally {
      setBtnLoading(false);
      setFormSpinner(false);
    }
  };

  const filteredLocationsList = useMemo(() => {
    if (!locationSearchTerm.trim()) return bulkLocationsList;
    const term = locationSearchTerm.toLowerCase();
    return bulkLocationsList.filter((loc) => {
      const locName = (loc?.name || "").toLowerCase();
      const locCity = (loc?.city || loc?.address?.city || "").toLowerCase();
      return locName.includes(term) || locCity.includes(term);
    });
  }, [bulkLocationsList, locationSearchTerm]);

  const activeLoc = bulkLocationsList[activeBulkIndex];
  const activeLocName = name || activeLoc?.name || "Location";

  const handleCloseNotification = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] select-none">
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleCloseNotification} severity={severity} />
      )}

      {formSpinner && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={formSpinner}
          className="flex flex-col"
        >
          <CircularProgress color="inherit" />
          <p className="mt-2 text-sm">{t("lang_loading") || "Processing..."}</p>
        </Backdrop>
      )}

      {/* Top Header Bar matching FAQ */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b border-[var(--border-color)] bg-[var(--secondary-bg-color)] shrink-0">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => handleRequestClose()}
            className="w-7 h-7 rounded-full flex items-center justify-center text-[var(--table-text-color)] hover:bg-[var(--focus-tab-color)] hover:text-[var(--primary-text-color)] transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h5
                style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 700 }}
                className="!text-sm !font-bold text-[var(--primary-text-color)] m-0 !leading-5"
              >
                {t("lang_bulk_edit_locations", "Bulk Edit Locations")}
              </h5>
              <span className="bg-[var(--focus-tab-color)] text-[var(--primary-text-color)] text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[var(--border-color)]">
                {bulkLocationsList.length} {t("lang_selected", "Selected")}
              </span>
            </div>
            <p className="text-[11px] text-[var(--table-text-color)] m-0 mt-0.5">
              Select any location from the list to view and edit its details.
            </p>
          </div>
        </div>

      </div>

      {/* Main Body (Split Layout) */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Sidebar Panel */}
        <div className="w-[300px] border-r border-[var(--border-color)] bg-[var(--primary-bg-color)] p-4 flex flex-col shrink-0">
          {/* Outer Rounded Container */}
          <div className="flex-1 flex flex-col border border-[var(--border-color)] rounded-2xl bg-[var(--secondary-bg-color)] overflow-hidden shadow-[var(--shadow-card)]">
            {/* Header section */}
            <div className="p-3.5 border-b border-[var(--border-color)] flex items-center justify-between">
              <span className="text-xs font-bold text-[var(--primary-text-color)]">
                Selected Locations
              </span>
              <div className="flex items-center space-x-1 text-xs text-[var(--table-text-color)]">
                <button
                  type="button"
                  disabled={activeBulkIndex === 0}
                  onClick={() => switchActiveLocation(activeBulkIndex - 1)}
                  className="p-1 rounded hover:bg-[var(--focus-tab-color)] disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="font-mono text-[11px] text-[var(--table-text-color)]">
                  {activeBulkIndex + 1}/{bulkLocationsList.length}
                </span>
                <button
                  type="button"
                  disabled={activeBulkIndex === bulkLocationsList.length - 1}
                  onClick={() => switchActiveLocation(activeBulkIndex + 1)}
                  className="p-1 rounded hover:bg-[var(--focus-tab-color)] disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Filter Search Input Section */}
            <div className="p-3 border-b border-[var(--border-color)] bg-transparent">
              <input
                type="text"
                placeholder="Filter selected locations..."
                value={locationSearchTerm}
                onChange={(e) => setLocationSearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-[var(--border-color)] rounded-lg bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] placeholder-[var(--table-text-color)] focus:outline-none focus:border-[var(--focus-border-color)] shadow-xs"
              />
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {filteredLocationsList.map((loc) => {
                const actualIndex = bulkLocationsList.findIndex((item) => item._id === loc._id);
                const isActive = actualIndex === activeBulkIndex;
                const isModified = isLocationModified(loc, actualIndex);
                const displayName = isActive
                  ? name || loc.name || "Untitled Location"
                  : bulkLocationsDataRef.current[loc._id]?.values?.name || loc.name || "Untitled Location";
                const displayCity = loc?.city || loc?.address?.city || "No City";

                return (
                  <div
                    key={loc._id}
                    onClick={() => switchActiveLocation(actualIndex)}
                    className={`p-3 cursor-pointer transition-colors flex items-center justify-between gap-3 border-b border-[var(--border-color)] last:border-b-0 ${isActive
                        ? "bg-[var(--focus-tab-color)]"
                        : "bg-[var(--secondary-bg-color)] hover:bg-[var(--focus-tab-color)]"
                      }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-md bg-[var(--primary-bg-color)] border border-[var(--border-color)] flex items-center justify-center shrink-0 overflow-hidden p-1 text-[var(--table-text-color)]">
                        <MapPin size={18} />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span
                          className={`text-xs truncate ${isActive
                              ? "font-bold text-[var(--primary-text-color)]"
                              : "font-medium text-[var(--primary-text-color)]"
                            }`}
                        >
                          {displayName}
                        </span>
                        <span className="text-[11px] text-[var(--table-text-color)] truncate mt-0.5">
                          {displayCity}
                        </span>
                      </div>
                    </div>

                    {isModified && (
                      <span
                        title='Modified'
                        className={`w-2 h-2 rounded-full bg-amber-500 shrink-0 select-none inline-block`}
                      />
                    )}
                  </div>
                );
              })}

              {filteredLocationsList.length === 0 && (
                <div className="p-4 text-center text-xs text-[var(--table-text-color)]">
                  No locations matching &quot;{locationSearchTerm}&quot;
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Main Form Content Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-[var(--primary-bg-color)]">
          {/* Right Sub-Header Bar */}
          <div className="flex items-center justify-between px-6 py-2.5 bg-[var(--secondary-bg-color)] border-b border-[var(--border-color)] text-xs shrink-0">
            <div>
              <span className="font-bold text-[var(--primary-text-color)]">
                Currently Editing: {activeLocName}
              </span>{" "}
              <span className="text-[var(--table-text-color)] font-mono text-[11px]">
                ({activeBulkIndex + 1} of {bulkLocationsList.length})
              </span>
            </div>
            <div>
              {modifiedCount > 0 ? (
                <span className="text-emerald-500 font-medium text-xs">
                  {modifiedCount} location modified
                </span>
              ) : (
                <span className="text-[var(--table-text-color)] text-xs font-normal">
                  No unsaved changes
                </span>
              )}
            </div>
          </div>

          {/* Form Area matching UpdatePosLocation */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            <form
              onKeyDown={(e) => e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.preventDefault()}
              className="max-w-4xl mx-auto space-y-5"
            >
              <div className="bg-[var(--secondary-bg-color)] border border-[var(--border-color)] rounded-2xl p-6 shadow-[var(--shadow-card)] flex flex-col gap-4">
                <InputComponent
                  id="name"
                  name="name"
                  label={t("lang_name")}
                  required={true}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setWarn(false);
                    setErr(false);
                  }}
                  error={
                    (err || warn) && !name.trim()
                      ? "Location Name is required"
                      : ""
                  }
                  placeholder={t("lang_name")}
                />

                <InputComponent
                  id="country"
                  name="country"
                  type="text"
                  label={t("lang_Country")}
                  disabled={true}
                  value={addressData?.country || "india"}
                  placeholder={t("lang_Country")}
                />

                <InputComponent
                  id="addressLine1"
                  name="addressLine1"
                  type="text"
                  label={t("lang_Address_line_1")}
                  value={addressData?.addressLine1}
                  onChange={(e) =>
                    setAddressData({ ...addressData, addressLine1: e.target.value })
                  }
                  placeholder="AddressLine 1"
                />

                <InputComponent
                  id="addressLine2"
                  name="addressLine2"
                  type="text"
                  label={t("lang_Address_line_2")}
                  value={addressData?.addressLine2}
                  onChange={(e) =>
                    setAddressData({ ...addressData, addressLine2: e.target.value })
                  }
                  placeholder="AddressLine 2"
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <InputComponent
                    id="Pincode"
                    name="pincode"
                    type="number"
                    label={t("lang_Pincode")}
                    required={true}
                    value={addressData?.pincode}
                    onChange={(e) => {
                      if (e.target.value.length <= 6) {
                        setAddressData({ ...addressData, pincode: e.target.value });
                      }
                    }}
                    onBlur={() => {
                      setErr(true);
                      handleGetDataFromPincode();
                    }}
                    placeholder="Pincode"
                    error={
                      addressData?.pincode?.length > 0 &&
                      addressData?.pincode?.length !== 6 &&
                      err
                        ? t("lang_Pincode_Validation")
                        : ""
                    }
                  />

                  <InputComponent
                    id="state"
                    name="state"
                    type="text"
                    label={t("lang_State")}
                    required={true}
                    disabled={true}
                    value={addressData?.state}
                    placeholder={t("lang_State")}
                  />

                  <InputComponent
                    id="city"
                    name="city"
                    type="text"
                    label={t("lang_City")}
                    required={true}
                    disabled={true}
                    value={addressData?.city}
                    placeholder={t("lang_City")}
                  />
                </div>

                <InputComponent
                  id="phone"
                  name="phone"
                  type="number"
                  label={t("lang_phone")}
                  value={addressData?.phone}
                  onChange={(e) => {
                    if (e.target.value.length <= 10) {
                      setAddressData({ ...addressData, phone: e.target.value });
                    }
                  }}
                  placeholder={t("lang_phone")}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Bar matching FAQ */}
      <div className="flex items-center justify-between w-full border-t border-[var(--border-color)] bg-[var(--secondary-bg-color)] p-3.5 px-6 shrink-0">
        <button
          type="button"
          onClick={() => handleRequestClose()}
          className="flex items-center text-xs font-medium text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors gap-1 cursor-pointer"
        >
          ← cancel
        </button>

        <div className="flex items-center gap-2">
          <ButtonComponent
            variant="primary"
            size="sm"
            loading={btnLoading}
            disabled={modifiedCount === 0 || btnLoading}
            onClick={handleSubmitBulkLocations}
          >
            {modifiedCount > 0 ? `Save Changes (${modifiedCount})` : "Save Changes"}
          </ButtonComponent>
        </div>
      </div>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onStay={handleStay}
        onLeave={handleConfirmLeave}
      />
    </div>
  );
};

export default BulkEditPosLocation;
